/* @jsx h */
import { defineComponent, h, onMounted, ref, type PropType } from "vue";
import type { GridColumnDef, CommitDir } from "./types";

export const TextEditor = defineComponent({
  name: "TextEditor",
  props: {
    column: { type: Object as PropType<GridColumnDef>, required: true },
    value: { type: [String, Number, null] as any, default: null },
    initialChar: { type: String as PropType<string | null>, default: null },
    onCommit: {
      type: Function as PropType<(value: any, dir: CommitDir) => void>,
      required: true,
    },
    onCancel: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const el = ref<HTMLInputElement | HTMLTextAreaElement>();

    // If user typed a char to start editing, use that char. Otherwise use existing value.
    const draft = ref<string>(
      props.initialChar !== null ? props.initialChar : String(props.value ?? ""),
    );

    onMounted(() => {
      const input = el.value;
      if (!input) return;
      input.focus();
      if (props.initialChar !== null) {
        // Typing-to-edit: cursor at end after the initial char
        const len = draft.value.length;
        input.setSelectionRange(len, len);
      } else {
        (input as HTMLInputElement).select?.();
      }
    });

    const commit = (dir: CommitDir = null) => props.onCommit(draft.value, dir);
    const cancel = () => props.onCancel();

    const onKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          e.stopPropagation();
          cancel();
          break;
        case "Enter":
          if (props.column.multiline && !e.metaKey && !e.ctrlKey) break; // allow newlines
          e.preventDefault();
          e.stopPropagation();
          commit("down");
          break;
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          commit(e.shiftKey ? "left" : "right");
          break;
      }
    };

    if (props.column.multiline) {
      return () => (
        <textarea
          ref={el}
          class="lg-editor-textarea"
          value={draft.value}
          placeholder={props.column.placeholder ?? ""}
          onInput={(e: any) => {
            draft.value = e.target.value;
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight + 2, 180) + "px";
          }}
          onBlur={() => commit(null)}
          onKeydown={onKeydown}
        />
      );
    }

    return () => (
      <input
        ref={el}
        type="text"
        class="lg-editor-input"
        value={draft.value}
        placeholder={props.column.placeholder ?? ""}
        onInput={(e: any) => (draft.value = e.target.value)}
        onBlur={() => commit(null)}
        onKeydown={onKeydown}
      />
    );
  },
});
